"use client";

import React from "react";
import { ShieldCheck, ChartLine, ArrowRight, Zap, Lock, ChartBar, Users, FileText, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const sectors = [{
  id: "financial",
  title: "Financial Services",
  description: "Modernize capital markets and private equity operations with high-precision AI systems designed for risk-adjusted performance.",
  icon: "chart",
  href: "/IndustriesPage"
}, {
  id: "healthcare",
  title: "Healthcare Systems",
  description: "Deploy secure, interoperable AI platforms that optimize clinical workflows and patient data intelligence at enterprise scale.",
  icon: "shield",
  href: "/IndustriesPage"
}, {
  id: "retail",
  title: "Enterprise Retail",
  description: "Transform global supply chains and consumer engagement through predictive demand sensing and automated inventory orchestration.",
  icon: "zap",
  href: "/IndustriesPage"
}, {
  id: "technology",
  title: "Technology & Platforms",
  description: "Scale digital engineering capabilities with AI-first architectures that accelerate product cycles and platform stability.",
  icon: "chart",
  href: "/IndustriesPage"
}, {
  id: "energy",
  title: "Oil, Gas & Energy",
  description: "Optimize energy production and distribution with AI-driven predictive maintenance and grid intelligence for the global transition.",
  icon: "zap",
  href: "/IndustriesPage"
}];
const useCases = [{
  sectorId: "financial",
  id: "fin-1",
  title: "Portfolio Optimization",
  content: "Achieve a 30% reduction in capital allocation cycle time by deploying neural networks that synthesize market volatility and liquidity risk in real-time.",
  icon: "bar-chart"
}, {
  sectorId: "financial",
  id: "fin-2",
  title: "Automated Compliance",
  content: "Integrate GRC and regulatory monitoring directly into trading platforms, ensuring 100% adherence to evolving SEC and FINRA standards without manual lag.",
  icon: "lock"
}, {
  sectorId: "financial",
  id: "fin-3",
  title: "Risk-Adjusted Execution",
  content: "Leverage predictive market insights to execute large-scale transformations, focusing on measurable revenue growth and operational cost containment.",
  icon: "activity"
}, {
  sectorId: "healthcare",
  id: "hea-1",
  title: "Clinical Data Intelligence",
  content: "Realize a 45% improvement in patient data processing speed using HIPAA-compliant AI models that automate diagnostic workflows and records management.",
  icon: "file"
}, {
  sectorId: "healthcare",
  id: "hea-2",
  title: "Operational Excellence",
  content: "Optimize resource utilization and facility management through predictive staffing models that align provider availability with real-time patient demand.",
  icon: "users"
}, {
  sectorId: "healthcare",
  id: "hea-3",
  title: "Secure Interoperability",
  content: "Build unified data layers that connect disparate health systems while maintaining the highest levels of cybersecurity via our integrated Cytrusst partnership.",
  icon: "shield"
}, {
  sectorId: "retail",
  id: "ret-1",
  title: "Predictive Supply Chain",
  content: "Drive a 22% lift in inventory turnover through demand sensing that synchronizes global logistics with hyper-local consumer trends and seasonal shifts.",
  icon: "zap"
}, {
  sectorId: "retail",
  id: "ret-2",
  title: "Personalization at Scale",
  content: "Implement AI-driven CRM platforms that deliver tailored experiences to millions of customers, increasing lifetime value and brand loyalty across all touchpoints.",
  icon: "users"
}, {
  sectorId: "retail",
  id: "ret-3",
  title: "Margin Optimization",
  content: "Identify and eliminate operational waste in the retail value chain using automated auditing and real-time cost-to-serve analysis for every SKU.",
  icon: "bar-chart"
}, {
  sectorId: "technology",
  id: "tec-1",
  title: "Platform Engineering",
  content: "Accelerate enterprise platform deployment by 60% with self-healing infrastructure and AI-driven code orchestration for complex digital ecosystems.",
  icon: "zap"
}, {
  sectorId: "technology",
  id: "tec-2",
  title: "Automated DevOps",
  content: "Streamline the software development lifecycle by integrating AI agents that handle testing, security scanning, and documentation in continuous pipelines.",
  icon: "file"
}, {
  sectorId: "technology",
  id: "tec-3",
  title: "Digital Transformation",
  content: "Modernize legacy systems into agile, cloud-native environments that support rapid innovation and seamless integration with emerging AI technologies.",
  icon: "activity"
}, {
  sectorId: "energy",
  id: "ene-1",
  title: "Predictive Maintenance",
  content: "Reduce unplanned downtime by 25% through sensor-fusion AI that predicts equipment failure in upstream and midstream assets before they occur.",
  icon: "activity"
}, {
  sectorId: "energy",
  id: "ene-2",
  title: "Grid Intelligence",
  content: "Optimize load balancing and renewable integration with real-time demand forecasting models that stabilize distribution networks.",
  icon: "zap"
}, {
  sectorId: "energy",
  id: "ene-3",
  title: "Operational Safety",
  content: "Enhance field safety and compliance monitoring using computer vision and automated reporting for high-risk industrial environments.",
  icon: "shield"
}];
const getSectorIcon = (iconName: string) => {
  switch (iconName) {
    case "shield":
      return <ShieldCheck className="size-10 text-primary" />;
    case "chart":
      return <ChartLine className="size-10 text-primary" />;
    default:
      return <Zap className="size-10 text-primary" />;
  }
};
const getUseCaseIcon = (iconName: string) => {
  switch (iconName) {
    case "zap":
      return <Zap className="size-4 text-primary" />;
    case "activity":
      return <Activity className="size-4 text-primary" />;
    case "lock":
      return <Lock className="size-4 text-primary" />;
    case "users":
      return <Users className="size-4 text-primary" />;
    case "file":
      return <FileText className="size-4 text-primary" />;
    case "bar-chart":
      return <ChartBar className="size-4 text-primary" />;
    default:
      return <Zap className="size-4 text-primary" />;
  }
};
export default function SectorSpecificPrecision() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="122751" id="vertical-specialization" className="bg-background relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute top-0 left-1/4 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 blur-[120px]" />
      <div className="pointer-events-none absolute right-0 bottom-0 h-[600px] w-[600px] translate-x-1/3 translate-y-1/3 rounded-full bg-chart-2/10 blur-[150px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center mb-16 transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            Precision for <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-chart-2">High-Impact Sectors</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Quantificial delivers domain-specific AI architectures designed to navigate the complex operational and performance demands of market-leading enterprises and private equity firms.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto transition-all duration-1000 delay-200 ${isGridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {sectors.map((sector, index) => <Card key={sector.id} data-index={index} className="group relative overflow-hidden bg-card/40 backdrop-blur-xl border-border/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 flex flex-col">
              {}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              
              <CardHeader className="pb-4 relative z-10">
                <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-lg bg-background/50 border border-border/50 shadow-sm group-hover:scale-110 transition-transform duration-500">
                  {getSectorIcon(sector.icon)}
                </div>
                <CardTitle className="text-2xl mb-2 text-foreground">{sector.title}</CardTitle>
                <CardDescription className="text-base leading-relaxed text-muted-foreground">
                  {sector.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="flex-grow relative z-10">
                <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4 font-mono">Outcome Metrics</h4>
                <Accordion type="single" collapsible className="w-full">
                  {useCases.filter(uc => uc.sectorId === sector.id).map((useCase, ucIndex) => <AccordionItem key={useCase.id} value={useCase.id} data-index={ucIndex} className="border-border/40">
                      <AccordionTrigger className="hover:no-underline hover:text-primary transition-colors py-3">
                        <div className="flex items-center gap-3 text-left">
                          <div className="p-1.5 rounded-sm bg-background/50 border border-border/30">
                            {getUseCaseIcon(useCase.icon)}
                          </div>
                          <span className="font-medium text-foreground">{useCase.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed pl-11 pr-4 pb-4">
                        {useCase.content}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
              
              <CardFooter className="pt-6 pb-8 relative z-10">
                <Button asChild variant="outline" className="w-full group/btn border-border/50 hover:border-primary/50 hover:bg-primary/5 rounded-md">
                  <Link to={sector.href}>
                    Explore {sector.title} Solutions
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>
      </div>
    </section>;
}